import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Globe, Shield, Database, Eye, Lock, UserCheck, Trash2, Mail, MapPin, Building2 } from 'lucide-react';

const PrivacyPage = () => {
  const lastUpdated = "December 13, 2024";
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-700/50 bg-slate-900/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">innovatorfoundervisas.com</span>
          </Link>
          <Link 
            to="/" 
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 text-emerald-400 px-4 py-2 rounded-full text-sm mb-6">
            <Shield className="w-4 h-4" />
            GDPR Compliant
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Privacy Policy & GDPR
          </h1>
          <p className="text-slate-400">
            Last updated: {lastUpdated}
          </p>
        </div>

        {/* GDPR Badge */}
        <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/30 rounded-2xl p-6 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <Shield className="w-8 h-8 text-emerald-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white mb-1">GDPR & UK GDPR Compliant</h3>
              <p className="text-slate-400">
                Cloud Lab Technologies - UK complies with the EU General Data Protection Regulation (GDPR) and the UK GDPR, ensuring your data rights are protected.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 p-8 md:p-12 space-y-8">
          
          {/* Company Information */}
          <section className="bg-slate-700/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Building2 className="w-6 h-6 text-emerald-500" />
              Data Controller Information
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>
                <strong className="text-white">innovatorfoundervisas.com</strong> is a product of <strong className="text-white">Cloud Lab Technologies - UK</strong>, a UK-based IT Consulting Firm owned by <strong className="text-white">Ahmad Raza</strong>, who is himself a UK Innovator Founder Visa holder and serves as a mentor on this platform.
              </p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Data Controller</p>
                  <p className="text-white font-semibold">Cloud Lab Technologies - UK</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Location</p>
                  <p className="text-white font-semibold">United Kingdom</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Owner</p>
                  <p className="text-white font-semibold">Ahmad Raza (UK Innovator Founder Visa Holder)</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <p className="text-slate-400 text-sm">Privacy Email</p>
                  <p className="text-white font-semibold">privacy@innovatorfoundervisas.com</p>
                </div>
              </div>
            </div>
          </section>

          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Eye className="w-6 h-6 text-emerald-500" />
              1. Introduction
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>
                Cloud Lab Technologies - UK, operating innovatorfoundervisas.com (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;), is committed to protecting your privacy and personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services.
              </p>
              <p>
                Cloud Lab Technologies - UK acts as the Data Controller for your personal data. This policy applies to all users worldwide, with specific provisions for users in the European Economic Area (EEA) and United Kingdom under GDPR.
              </p>
            </div>
          </section>

          {/* Data We Collect */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Database className="w-6 h-6 text-emerald-500" />
              2. Information We Collect
            </h2>
            <div className="text-slate-300 space-y-6">
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-3">2.1 Information You Provide</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Account Information:</strong> Name, email address, phone number, country</li>
                  <li><strong className="text-white">Business Information:</strong> Business ideas, plans, and documents you create using our tools</li>
                  <li><strong className="text-white">Payment Information:</strong> Processed securely by third-party providers (Stripe, PayPal) - we do not store card details</li>
                  <li><strong className="text-white">Communication Data:</strong> Support tickets, feedback, and correspondence</li>
                  <li><strong className="text-white">Interview Responses:</strong> Audio recordings and transcripts from mock interviews (with your consent)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-white mb-3">2.2 Automatically Collected Information</h3>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong className="text-white">Device Information:</strong> Browser type, operating system, device identifiers</li>
                  <li><strong className="text-white">Usage Data:</strong> Pages visited, features used, time spent on platform</li>
                  <li><strong className="text-white">Log Data:</strong> IP address, access times, referring URLs</li>
                  <li><strong className="text-white">Cookies:</strong> As described in our Cookie section below</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Legal Basis */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Lock className="w-6 h-6 text-emerald-500" />
              3. Legal Basis for Processing (GDPR)
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>Under GDPR/UK GDPR, Cloud Lab Technologies - UK processes your data based on the following legal grounds:</p>
              <div className="overflow-x-auto">
                <table className="w-full mt-4 border-collapse">
                  <thead>
                    <tr className="bg-slate-700/50">
                      <th className="border border-slate-600 px-4 py-3 text-left text-white">Purpose</th>
                      <th className="border border-slate-600 px-4 py-3 text-left text-white">Legal Basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-600 px-4 py-3">Providing our services</td>
                      <td className="border border-slate-600 px-4 py-3">Contract performance</td>
                    </tr>
                    <tr className="bg-slate-700/20">
                      <td className="border border-slate-600 px-4 py-3">Processing payments</td>
                      <td className="border border-slate-600 px-4 py-3">Contract performance</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-600 px-4 py-3">Customer support</td>
                      <td className="border border-slate-600 px-4 py-3">Legitimate interest</td>
                    </tr>
                    <tr className="bg-slate-700/20">
                      <td className="border border-slate-600 px-4 py-3">Marketing communications</td>
                      <td className="border border-slate-600 px-4 py-3">Consent</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-600 px-4 py-3">Audio recording (interviews)</td>
                      <td className="border border-slate-600 px-4 py-3">Explicit consent</td>
                    </tr>
                    <tr className="bg-slate-700/20">
                      <td className="border border-slate-600 px-4 py-3">Legal compliance</td>
                      <td className="border border-slate-600 px-4 py-3">Legal obligation</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-600 px-4 py-3">Fraud prevention</td>
                      <td className="border border-slate-600 px-4 py-3">Legitimate interest</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* How We Use Data */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. How We Use Your Information</h2>
            <div className="text-slate-300 space-y-4">
              <p>Cloud Lab Technologies - UK uses your information to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide and maintain our Platform and Services</li>
                <li>Process your transactions and manage your account</li>
                <li>Generate AI-powered business plans and interview feedback</li>
                <li>Send service-related communications and updates</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Improve our Platform through analytics and feedback</li>
                <li>Send marketing communications (with your consent)</li>
                <li>Comply with legal obligations</li>
                <li>Protect against fraud and unauthorized access</li>
              </ul>
            </div>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. Data Sharing and Third Parties</h2>
            <div className="text-slate-300 space-y-4">
              <p>Cloud Lab Technologies - UK may share your data with:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Payment Processors:</strong> Stripe, PayPal (for payment processing)</li>
                <li><strong className="text-white">AI Service Providers:</strong> For generating business plans and interview analysis</li>
                <li><strong className="text-white">Email Service Providers:</strong> SendGrid (for transactional emails)</li>
                <li><strong className="text-white">Analytics Providers:</strong> For platform improvement</li>
                <li><strong className="text-white">Cloud Hosting:</strong> MongoDB, cloud infrastructure providers</li>
                <li><strong className="text-white">Legal Authorities:</strong> When required by law or legal process</li>
              </ul>
              <p className="mt-4">
                We do NOT sell your personal data to third parties. All third-party processors are bound by data processing agreements compliant with GDPR.
              </p>
            </div>
          </section>

          {/* International Transfers */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <MapPin className="w-6 h-6 text-emerald-500" />
              6. International Data Transfers
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>
                Your data may be transferred to and processed in countries outside the EEA/UK. When Cloud Lab Technologies - UK transfers data internationally, we ensure appropriate safeguards are in place:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                <li>UK International Data Transfer Agreement (IDTA) where applicable</li>
                <li>Adequacy decisions by relevant authorities</li>
                <li>Binding corporate rules where applicable</li>
              </ul>
            </div>
          </section>

          {/* Your Rights */}
          <section className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4">7. Your Data Rights (GDPR)</h2>
            <div className="text-slate-300 space-y-4">
              <p className="text-emerald-400 font-semibold">Under GDPR/UK GDPR, you have the following rights:</p>
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">Right of Access</h4>
                  <p className="text-sm">Request a copy of your personal data</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">Right to Rectification</h4>
                  <p className="text-sm">Request correction of inaccurate data</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">Right to Erasure</h4>
                  <p className="text-sm">&ldquo;Right to be forgotten&rdquo; - request deletion</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">Right to Restrict Processing</h4>
                  <p className="text-sm">Limit how we use your data</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">Right to Data Portability</h4>
                  <p className="text-sm">Receive your data in a portable format</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg">
                  <h4 className="text-white font-semibold mb-2">Right to Object</h4>
                  <p className="text-sm">Object to processing based on legitimate interest</p>
                </div>
                <div className="bg-slate-800/50 p-4 rounded-lg md:col-span-2">
                  <h4 className="text-white font-semibold mb-2">Right to Withdraw Consent</h4>
                  <p className="text-sm">Withdraw consent at any time where processing is based on consent</p>
                </div>
              </div>
              <p className="mt-4">
                To exercise these rights, contact Cloud Lab Technologies - UK at <strong className="text-white">privacy@innovatorfoundervisas.com</strong>. We will respond within 30 days.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Trash2 className="w-6 h-6 text-emerald-500" />
              8. Data Retention
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>Cloud Lab Technologies - UK retains your personal data for:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">Account Data:</strong> Duration of your account plus 2 years after deletion request</li>
                <li><strong className="text-white">Business Plans/Documents:</strong> Duration of your account</li>
                <li><strong className="text-white">Transaction Records:</strong> 7 years (legal/tax requirements)</li>
                <li><strong className="text-white">Support Communications:</strong> 3 years</li>
                <li><strong className="text-white">Marketing Consent Records:</strong> Duration of consent plus 3 years</li>
              </ul>
              <p>
                After retention periods expire, data is securely deleted or anonymized.
              </p>
            </div>
          </section>

          {/* Cookies */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Cookies and Tracking</h2>
            <div className="text-slate-300 space-y-4">
              <p>We use cookies and similar technologies:</p>
              <div className="overflow-x-auto">
                <table className="w-full mt-4 border-collapse">
                  <thead>
                    <tr className="bg-slate-700/50">
                      <th className="border border-slate-600 px-4 py-3 text-left text-white">Type</th>
                      <th className="border border-slate-600 px-4 py-3 text-left text-white">Purpose</th>
                      <th className="border border-slate-600 px-4 py-3 text-left text-white">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-slate-600 px-4 py-3">Essential</td>
                      <td className="border border-slate-600 px-4 py-3">Authentication, security</td>
                      <td className="border border-slate-600 px-4 py-3">Session</td>
                    </tr>
                    <tr className="bg-slate-700/20">
                      <td className="border border-slate-600 px-4 py-3">Functional</td>
                      <td className="border border-slate-600 px-4 py-3">Preferences, settings</td>
                      <td className="border border-slate-600 px-4 py-3">1 year</td>
                    </tr>
                    <tr>
                      <td className="border border-slate-600 px-4 py-3">Analytics</td>
                      <td className="border border-slate-600 px-4 py-3">Usage statistics</td>
                      <td className="border border-slate-600 px-4 py-3">2 years</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="mt-4">
                You can manage cookie preferences through your browser settings. Note that disabling essential cookies may affect Platform functionality.
              </p>
            </div>
          </section>

          {/* Security */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">10. Data Security</h2>
            <div className="text-slate-300 space-y-4">
              <p>Cloud Lab Technologies - UK implements appropriate technical and organizational measures to protect your data:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Encryption in transit (TLS/SSL) and at rest</li>
                <li>Secure password hashing (bcrypt)</li>
                <li>Regular security assessments and audits</li>
                <li>Access controls and authentication measures</li>
                <li>Employee training on data protection</li>
                <li>Incident response procedures</li>
              </ul>
              <p>
                While we strive to protect your data, no method of transmission over the Internet is 100% secure. We encourage you to use strong passwords and protect your account credentials.
              </p>
            </div>
          </section>

          {/* Children */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">11. Children&apos;s Privacy</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                Our Platform is not intended for individuals under 18 years of age. We do not knowingly collect personal data from children. If you believe we have collected data from a child, please contact us immediately.
              </p>
            </div>
          </section>

          {/* Complaints */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">12. Complaints</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                If you have concerns about our data practices, please contact Cloud Lab Technologies - UK first. You also have the right to lodge a complaint with a supervisory authority:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong className="text-white">UK:</strong> Information Commissioner&apos;s Office (ICO) - ico.org.uk</li>
                <li><strong className="text-white">EU:</strong> Your local Data Protection Authority</li>
              </ul>
            </div>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">13. Changes to This Policy</h2>
            <div className="text-slate-300 space-y-4">
              <p>
                Cloud Lab Technologies - UK may update this Privacy Policy from time to time. We will notify you of material changes by email or prominent notice on our Platform. The &ldquo;Last updated&rdquo; date at the top indicates when the policy was last revised.
              </p>
            </div>
          </section>

          {/* Contact */}
          <section className="bg-slate-700/30 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
              <Mail className="w-6 h-6 text-emerald-500" />
              14. Contact Us
            </h2>
            <div className="text-slate-300 space-y-4">
              <p>For privacy-related inquiries:</p>
              <ul className="space-y-2">
                <li><strong className="text-white">Company:</strong> Cloud Lab Technologies - UK</li>
                <li><strong className="text-white">Owner:</strong> Ahmad Raza (UK Innovator Founder Visa Holder)</li>
                <li><strong className="text-white">Privacy Email:</strong> privacy@innovatorfoundervisas.com</li>
                <li><strong className="text-white">Data Protection Officer:</strong> dpo@innovatorfoundervisas.com</li>
                <li><strong className="text-white">General Support:</strong> support@innovatorfoundervisas.com</li>
                <li><strong className="text-white">Website:</strong> innovatorfoundervisas.com</li>
              </ul>
            </div>
          </section>

        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/terms" 
            className="px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            Terms & Conditions
          </Link>
          <Link 
            to="/refund-policy" 
            className="px-6 py-3 bg-slate-700/50 hover:bg-slate-700 text-white rounded-lg transition-colors"
          >
            Refund Policy
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-700/50 mt-12 py-8">
        <div className="max-w-5xl mx-auto px-4 text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} Cloud Lab Technologies - UK. All rights reserved.</p>
          <p className="mt-1">innovatorfoundervisas.com is a product of Cloud Lab Technologies - UK</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPage;
